import { useState, useCallback } from 'react';
import { Symbol, Basket } from '../../types';
import { parseSymbols } from '../../utils/symbolsUtils';

export const useSymbolSelection = (initialSymbols?: Symbol[] | string | any) => {
  const [symbolInput, setSymbolInput] = useState('');
  const [symbolDirection, setSymbolDirection] = useState<'long' | 'short' | 'auto'>('long');
  const [symbols, setSymbols] = useState<Symbol[]>(() => {
    if (Array.isArray(initialSymbols)) {
      return initialSymbols.map(s => {
        // Ensure each symbol has the required properties
        if (typeof s === 'object') {
          return {
            ticker: s.ticker || s.symbol || s.name || '',
            symbol: s.symbol || s.ticker || s.name || '',
            direction: s.direction || 'long',
            weight: s.weight || 1
          } as Symbol;
        } else {
          return {
            ticker: String(s),
            symbol: String(s),
            direction: 'long' as const,
            weight: 1
          } as Symbol;
        }
      });
    } else if (typeof initialSymbols === 'string' && initialSymbols.trim()) {
      return parseSymbols(initialSymbols);
    }
    return [];
  });

  const handleAddSymbol = useCallback((symbol: string, direction: 'long' | 'short' | 'auto' = 'long', symbolData?: Partial<Symbol>) => {
    if (!symbol.trim()) return;
    
    const newSymbol: Symbol = {
      ticker: symbol.trim(),
      symbol: symbol.trim(),
      direction: direction,
      weight: 1,
      // Include additional properties from symbolData if provided
      ...(symbolData || {})
    };
    
    setSymbols(prev => {
      const exists = prev.some(s => (s.ticker || s.symbol)?.toLowerCase() === symbol.toLowerCase());
      if (exists) return prev;
      return [...prev, newSymbol];
    });
    
    setSymbolInput('');
  }, []);

  const handleRemoveSymbol = useCallback((symbolToRemove: string) => {
    setSymbols(prev => prev.filter(s => s.ticker !== symbolToRemove && s.symbol !== symbolToRemove));
  }, []);

  // Modified to accept Basket object to get profit/loss parameters
  const handleBasketSelect = useCallback((basket: Basket) => {
    const basketSymbols = basket.symbols || [];
    basketSymbols.forEach(symbol => {
      // Apply basket's profit target, stop loss, and VaR to each symbol
      handleAddSymbol(
        symbol.ticker || symbol.symbol || '', 
        symbol.direction || 'auto', 
        {
          // Apply basket parameters to each symbol
          profitTarget: basket.profitTarget,
          stopLoss: basket.stopLoss,
          valueAtRisk: basket.valueAtRisk,
          // Keep any existing symbol-specific parameters
          ...(symbol.profitTarget ? { profitTarget: symbol.profitTarget } : {}),
          ...(symbol.stopLoss ? { stopLoss: symbol.stopLoss } : {}),
          ...(symbol.valueAtRisk ? { valueAtRisk: symbol.valueAtRisk } : {})
        }
      );
    });
    
    // Return the basket to the parent for risk parameter handling
    return basket;
  }, [handleAddSymbol]);

  return {
    symbolInput,
    symbolDirection,
    symbols,
    setSymbolInput,
    setSymbolDirection,
    handleBasketSelect,
    handleAddSymbol,
    handleRemoveSymbol,
  };
};
